import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import CandlestickChart from '../CandlestickChart';
import {
  getAlgorithmDisplayName,
  AlgorithmProvider
} from '../../utils/algorithmProvider';
import { FavoriteButton } from '../FavoriteButton';
import { FavoriteSnapshot, updateCompanyInfo, isFavorite } from '../../services/favoritesService';
import { API_BASE_URL } from '../../services/api';

interface PriceData {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface DetectionResult {
  symbol: string;
  company: string;
  detected: boolean;
  confidence_score: number;
  change_date: string;
  stage: '確定' | '兆候';
  detection_reason: string;
  metrics: any;
  isNew?: boolean;  // NEWバッジ表示フラグ
  parameters?: Record<string, any>;  // 検出に使用されたパラメータ
  algorithm: string;  // 使用されたアルゴリズムID
  required_days?: number;  // 検出に必要な日数（バックエンドで計算済み）
  detection_date?: string;  // 検出実行日（お気に入りからの場合）
}

interface CompanyTopic {
  title: string;
  summary: string;
  source_url: string;
  pub_date?: string;
}

interface CompanyInfo {
  symbol: string;
  company_name: string;
  business_overview: string;
  strengths: string[];
  weaknesses: string[];
  growth_outlook: string;
  risks: string[];
  recent_topics: CompanyTopic[];
  last_updated: string;
  raw_content?: string;
}

const StockDetail: React.FC = () => {
  const { symbol } = useParams<{ symbol: string }>();
  const [loading, setLoading] = useState(true);
  const [priceData, setPriceData] = useState<PriceData[]>([]);
  const [detectionResult, setDetectionResult] = useState<DetectionResult | null>(null);

  // 企業情報関連の状態
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [companyInfoLoading, setCompanyInfoLoading] = useState(false);
  const [companyInfoError, setCompanyInfoError] = useState<string | null>(null);

  // 安全な数値フォーマット関数
  const formatNumber = (value: any, decimals: number = 1, defaultValue: string = "N/A"): string => {
    if (value === null || value === undefined || value === "" || isNaN(Number(value))) {
      return defaultValue;
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return defaultValue;
    }
    return numValue.toFixed(decimals);
  };

  useEffect(() => {
    const loadStockData = async () => {
      if (!symbol) return;

      try {
        // AlgorithmProviderを初期化（アルゴリズム情報が必要なため）
        await AlgorithmProvider.fetchAlgorithmsFromBackend();

        console.log(`${symbol}のデータを読み込み中...`);
        
        // localStorageから検出結果を取得
        let actualResult: DetectionResult | null = null;
        let algorithmUsed: string | null = null;
        
        // まず選択されたデータを確認
        const selectedStockData = localStorage.getItem('selectedStockData');
        if (selectedStockData) {
          const selectedStock = JSON.parse(selectedStockData);
          console.log('🎯 [StockDetail] 選択された銘柄データ:', selectedStock);
          console.log('🔍 [StockDetail] selectedStock.parameters:', selectedStock.parameters);
          
          // 選択されたデータが現在のシンボルと一致する場合はそのまま使用
          if (selectedStock.symbol === symbol) {
            if (!selectedStock.algorithm) {
              console.error('❌ [StockDetail] selectedStock.algorithmが未定義:', selectedStock);
              throw new Error('algorithm is required');
            }
            algorithmUsed = selectedStock.algorithm;

            if (!algorithmUsed) {
              console.error('❌ [StockDetail] algorithmUsedがnullです');
              throw new Error('algorithmUsed is null');
            }

            actualResult = {
              symbol: selectedStock.symbol,
              company: selectedStock.company || `${symbol} Inc.`,
              detected: true,
              confidence_score: selectedStock.confidence_score,
              change_date: selectedStock.change_date,
              stage: selectedStock.stage,
              detection_reason: selectedStock.detection_reason || `変化点検出アルゴリズム（${algorithmUsed}）により検出されました。`,
              metrics: selectedStock.metrics || {},
              isNew: selectedStock.isNew || false,  // NEWフラグを取得
              parameters: selectedStock.parameters || {},  // パラメータを追加
              algorithm: algorithmUsed,  // アルゴリズムIDを追加
              required_days: selectedStock.required_days  // 必要日数を追加
            };
            console.log(`✅ [StockDetail] 選択データを使用: stage="${actualResult.stage}", algorithm="${algorithmUsed}", required_days=${actualResult.required_days}`);
          }
        }
        
        // 選択データがない場合は従来の方法でデータを取得
        if (!actualResult) {
          const savedStocks = localStorage.getItem('detectedStocks');
          if (savedStocks) {
            const stocksArray = JSON.parse(savedStocks);
            const foundStock = stocksArray.find((s: any) => s.symbol === symbol);
            if (foundStock) {
              if (!foundStock.algorithm) {
                console.error('❌ [StockDetail] foundStock.algorithmが未定義:', foundStock);
                throw new Error('algorithm is required');
              }
              algorithmUsed = foundStock.algorithm;

              if (!algorithmUsed) {
                console.error('❌ [StockDetail] algorithmUsedがnullです');
                throw new Error('algorithmUsed is null');
              }

              actualResult = {
                symbol: foundStock.symbol,
                company: foundStock.company || `${symbol} Inc.`,
                detected: true,
                confidence_score: foundStock.confidence_score,
                change_date: foundStock.change_date,
                stage: foundStock.stage,
                detection_reason: foundStock.detection_reason || `変化点検出アルゴリズム（${algorithmUsed}）により検出されました。`,
                metrics: foundStock.metrics || {},
                isNew: foundStock.isNew || false,  // NEWフラグを取得
                parameters: foundStock.parameters || {},  // パラメータを追加
                algorithm: algorithmUsed,  // アルゴリズムIDを追加
                required_days: foundStock.required_days  // 必要日数を追加
              };
              console.log(`⚠️ [StockDetail] フォールバック: 最初のデータを使用, required_days=${actualResult.required_days}`);
            }
          }
        }
        
        // localStorageにデータがない場合はエラー表示
        if (!actualResult) {
          console.error(`❌ [StockDetail] ${symbol}のデータがlocalStorageに見つかりません`);
          console.error(`❌ [StockDetail] actualResult:`, actualResult);
          return; // データがない場合はreturnして表示しない
        }

        // actualResultは既にalgorithmプロパティを含んでいるので、そのまま設定
        setDetectionResult(actualResult);
        console.log(`🔴 [StockDetail] 最終的な detectionResult.stage: "${actualResult.stage}", algorithm: "${actualResult.algorithm}"`);

        // 検出実行日とrequired_daysを使って株価データを取得
        // detection_date（検出を実行した日）またはmetrics.end_date（分析期間の終了日）を基準日とする
        const requiredDays = actualResult.required_days || 30; // フォールバック: デフォルト30日

        // 検出実行日を取得：お気に入りから来た場合はdetection_date、それ以外はmetrics.end_date
        let detectionExecutionDate = actualResult.detection_date;
        if (!detectionExecutionDate && actualResult.metrics?.end_date) {
          detectionExecutionDate = actualResult.metrics.end_date;
        }

        // ISO形式の場合は日付部分のみ抽出（YYYY-MM-DD）
        if (detectionExecutionDate && detectionExecutionDate.includes('T')) {
          detectionExecutionDate = detectionExecutionDate.split('T')[0];
        }

        // 検出実行日がない場合はend_dateパラメータなし（最新日から取得）
        const endDate = detectionExecutionDate;

        console.log(`📊 [StockDetail] 株価データ取得: ${endDate ? `${endDate}から` : '最新日から'}${requiredDays}日分遡る`);

        const historyResponse = await fetch(`${API_BASE_URL}/stocks/${symbol}/history?${endDate ? `end_date=${endDate}&` : ''}days=${requiredDays}`);
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          console.log(`✅ [StockDetail] APIレスポンス: ${historyData.history.length}件のデータを受信`);
          console.log(`✅ [StockDetail] 期間: ${historyData.end_date}から${requiredDays}日分`);
          setPriceData(historyData.history);
        } else {
          console.error(`❌ [StockDetail] 株価データ取得失敗: ${historyResponse.status}`);
        }

      } catch (error) {
        console.error('データ読み込みエラー:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStockData();

    // LocalStorageから企業情報のキャッシュを読み込み
    const cachedCompanyInfo = localStorage.getItem(`companyInfo_${symbol}`);
    if (cachedCompanyInfo) {
      try {
        const parsedInfo = JSON.parse(cachedCompanyInfo);
        setCompanyInfo(parsedInfo);
        console.log(`✅ ${symbol}の企業情報をキャッシュから読み込み`);
      } catch (error) {
        console.error('企業情報キャッシュの読み込みエラー:', error);
      }
    }
  }, [symbol]);

  // 企業情報を取得する関数
  const fetchCompanyInfo = async () => {
    if (!symbol || !detectionResult) return;

    setCompanyInfoLoading(true);
    setCompanyInfoError(null);

    try {
      console.log(`📊 ${symbol}の企業情報を取得中...`);

      const response = await fetch(
        `${API_BASE_URL}/stocks/${symbol}/company-info?company_name=${encodeURIComponent(detectionResult.company)}`
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '企業情報の取得に失敗しました');
      }

      const data: CompanyInfo = await response.json();

      // 状態を更新
      setCompanyInfo(data);

      // LocalStorageにキャッシュを保存
      localStorage.setItem(`companyInfo_${symbol}`, JSON.stringify(data));

      // お気に入り登録済みの場合はスナップショットも更新
      if (isFavorite(symbol)) {
        try {
          updateCompanyInfo(symbol, {
            company_overview: data.business_overview,
            strengths: data.strengths,
            weaknesses: data.weaknesses,
            growth_prospects: data.growth_outlook,
            recent_topics: data.recent_topics,
            last_updated: data.last_updated
          });
          console.log(`✅ ${symbol}のお気に入りスナップショットにOpenAI情報を反映`);
        } catch (error) {
          console.error(`⚠️ ${symbol}のスナップショット更新に失敗:`, error);
        }
      }

      console.log(`✅ ${symbol}の企業情報取得完了`);
    } catch (error: any) {
      console.error(`❌ ${symbol}の企業情報取得エラー:`, error);
      setCompanyInfoError(error.message || '企業情報の取得に失敗しました');
    } finally {
      setCompanyInfoLoading(false);
    }
  };


  // CSV書出し機能
  const handleExportCSV = () => {
    if (!priceData || priceData.length === 0) {
      alert('株価データがありません');
      return;
    }
    console.log(`★ CSV書き出し: priceDataは${priceData.length}件`);

    // データの日付範囲を取得
    const dates = priceData.map(d => new Date(d.date)).sort((a, b) => a.getTime() - b.getTime());
    const startDate = dates[0].toISOString().split('T')[0];
    const endDate = dates[dates.length - 1].toISOString().split('T')[0];
    
    // デフォルトファイル名を生成
    const defaultFileName = `${symbol}_${startDate}-${endDate}.csv`;

    // CSVデータを生成
    const csvHeader = 'Date,Open,High,Low,Close,Volume\n';
    const csvRows = priceData
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) // 日付順にソート
      .map(row => `${row.date},${row.open},${row.high},${row.low},${row.close},${row.volume}`)
      .join('\n');
    
    const csvContent = csvHeader + csvRows;
    
    // ダウンロード処理
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', defaultFileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (loading || !detectionResult) {
    return (
      <div className="stock-detail">
        <div className="loading">
          <h2>{symbol} のデータを読み込み中...</h2>
        </div>
      </div>
    );
  }

  // アルゴリズム情報をAlgorithmProviderから取得（Single Source of Truth）
  if (!(detectionResult as any).algorithm) {
    console.error('❌ [StockDetail] detectionResult.algorithmが未定義:', detectionResult);
    throw new Error('algorithm is required');
  }
  const currentAlgorithm = (detectionResult as any).algorithm;
  const algorithmBackendInfo = AlgorithmProvider.getAlgorithmByKey(currentAlgorithm);

  if (!algorithmBackendInfo) {
    console.error('❌ [StockDetail] アルゴリズム情報が取得できません:', currentAlgorithm);
    throw new Error(`Algorithm info not found for: ${currentAlgorithm}`);
  }

  // UI表示用のアルゴリズム情報
  const algorithmInfo = {
    title: algorithmBackendInfo.display_name_ja,
    description: algorithmBackendInfo.description_ja,
    characteristics: algorithmBackendInfo.characteristics || []
  };

  // お気に入り用のスナップショットを作成
  // アルゴリズムIDの検証
  if (!detectionResult.algorithm) {
    console.error('❌ [StockDetail] アルゴリズムIDが未定義です:', detectionResult);
    throw new Error(`アルゴリズムIDが見つかりません。detectionResult: ${JSON.stringify(detectionResult)}`);
  }

  const favoriteSnapshot: FavoriteSnapshot = {
    symbol: detectionResult.symbol,
    company_name: detectionResult.company,
    detection_date: new Date().toISOString(),
    detection_result: {
      symbol: detectionResult.symbol,
      company_name: detectionResult.company,
      detection_date: new Date().toISOString(),
      change_point_date: detectionResult.change_date,
      confidence_score: detectionResult.confidence_score,
      algorithm_id: detectionResult.algorithm, // detectionResult.algorithmを使用（フォールバック禁止）
      algorithm_name: algorithmInfo.title,
      parameters: detectionResult.parameters || {},
      detection_reason: detectionResult.detection_reason,
      metrics: detectionResult.metrics,
      required_days: detectionResult.required_days  // 必要日数を含める
    },
    company_info: companyInfo ? {
      company_overview: companyInfo.business_overview,
      strengths: companyInfo.strengths,
      weaknesses: companyInfo.weaknesses,
      growth_prospects: companyInfo.growth_outlook,
      recent_topics: companyInfo.recent_topics,
      last_updated: companyInfo.last_updated
    } : undefined,
    added_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  // お気に入りボタンのトグル時にOpenAI情報も更新
  const handleFavoriteToggle = (isFavorited: boolean) => {
    if (isFavorited && companyInfo) {
      // お気に入り登録時にOpenAI情報があれば保存
      try {
        updateCompanyInfo(detectionResult.symbol, {
          company_overview: companyInfo.business_overview,
          strengths: companyInfo.strengths,
          weaknesses: companyInfo.weaknesses,
          growth_prospects: companyInfo.growth_outlook,
          recent_topics: companyInfo.recent_topics,
          last_updated: companyInfo.last_updated
        });
      } catch (error) {
        console.error('OpenAI情報の更新に失敗:', error);
      }
    }
  };

  return (
    <div className="stock-detail">
      <div className="detail-header">
        <div className="header-title-row">
          <FavoriteButton
            snapshot={favoriteSnapshot}
            size="large"
            onToggle={handleFavoriteToggle}
          />
          <h2>
            {detectionResult.symbol} - {detectionResult.company}
            {detectionResult.isNew && <span className="new-badge detail-new-badge">NEW</span>}
          </h2>
        </div>
        <button onClick={handleExportCSV} className="export-csv-button">
          📊 株価データの書出し
        </button>
      </div>

      <div className="detection-summary">
        <div className="summary-item">
          <span className="label">検出状況:</span>
          <span className="value detected">{detectionResult.detected ? '変化点検出済み' : '変化点未検出'}</span>
        </div>
        <div className="summary-item">
          <span className="label">アルゴリズム:</span>
          <span className="value">
            {(() => {
              if (!(detectionResult as any).algorithm) {
                console.error('❌ [StockDetail] detectionResult.algorithmが未定義 (表示用):', detectionResult);
                return '⚠️ 未定義';
              }
              return getAlgorithmDisplayName((detectionResult as any).algorithm);
            })()}
          </span>
        </div>
        <div className="summary-item">
          <span className="label">信頼度:</span>
          <span className="value">{Math.floor(detectionResult.confidence_score)}%</span>
        </div>
        <div className="summary-item">
          <span className="label">段階:</span>
          <span className={`value ${detectionResult.stage === '確定' ? 'confirmed' : 'indication'}`}>
            {detectionResult.stage}
          </span>
        </div>
        {(detectionResult as any).algorithm !== 'linear_uptrend' && (
          <div className="summary-item">
            <span className="label">変化点日付:</span>
            <span className="value">{detectionResult.change_date}</span>
          </div>
        )}
        {(detectionResult as any).algorithm === 'linear_uptrend' && (
          <div className="summary-item">
            <span className="label">分析開始日:</span>
            <span className="value">{((detectionResult as any).metrics?.start_date || detectionResult.change_date)}</span>
          </div>
        )}
      </div>

      <div className="chart-section">
        <h3>ローソク足チャート</h3>
        
        {/* ハイパーパラメータ表示エリア */}
        <div className="hyperparameters-section">
          <h4>検出に使用されたハイパーパラメータ</h4>
          <div className="hyperparameters-grid">
            {(() => {
              if (!(detectionResult as any).algorithm) {
                console.error('❌ [StockDetail] detectionResult.algorithmが未定義 (パラメータ表示用):', detectionResult);
                return <p>⚠️ アルゴリズム情報が取得できませんでした</p>;
              }
              const algorithmKey = (detectionResult as any).algorithm;
              const algorithmInfo = AlgorithmProvider.getAlgorithmByKey(algorithmKey);
              const detectedParams = (detectionResult as any).parameters || {};
              
              // デバッグ：パラメータの詳細を確認
              console.log('🔍 [StockDetail] detectionResult.parameters:', (detectionResult as any).parameters);
              console.log('🔍 [StockDetail] detectedParams:', detectedParams);
              console.log('🔍 [StockDetail] 完全な detectionResult:', detectionResult);
              
              if (!algorithmInfo) {
                return <p>パラメータ情報が取得できませんでした</p>;
              }
              
              return algorithmInfo.parameters_metadata.map((param) => {
                const currentValue = detectedParams[param.key] ?? algorithmInfo.default_params[param.key];
                return (
                  <div key={param.key} className="hyperparam-item">
                    <span className="hyperparam-label">
                      {param.display_name_ja}
                      {param.unit && <span className="unit">({param.unit})</span>}:
                    </span>
                    <span className="hyperparam-value">
                      {typeof currentValue === 'boolean' 
                        ? (currentValue ? '有効' : '無効')
                        : (typeof currentValue === 'number') 
                          ? (isNaN(currentValue) ? 'N/A' : (currentValue % 1 !== 0 ? currentValue.toFixed(2) : currentValue))
                          : (currentValue ?? 'N/A')
                      }
                    </span>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {priceData.length > 0 ? (
          (() => {
            const currentAlgo = (detectionResult as any).algorithm;
            const metrics = (detectionResult as any).metrics || {};

            // linear_uptrendの場合は表示期間を制限
            let displayData = priceData;
            if (currentAlgo === 'linear_uptrend' && metrics.analysis_period_days) {
              // 最新から指定日数分のデータのみ表示
              displayData = priceData.slice(0, metrics.analysis_period_days);
            }

            // linear_uptrendの場合は変化点を表示しない
            const shouldShowChangePoint = currentAlgo !== 'linear_uptrend';

            return (
              <CandlestickChart
                data={displayData}
                symbol={detectionResult.symbol}
                changePointDate={shouldShowChangePoint ? detectionResult.change_date : undefined}
              />
            );
          })()
        ) : (
          <div className="chart-placeholder">
            <p>チャートデータを読み込み中...</p>
          </div>
        )}
      </div>

      <div className="analysis-detail">
        <h3>分析詳細</h3>
        
        <div className="detection-reason">
          <h4>検出理由</h4>
          <p>{detectionResult.detection_reason}</p>
        </div>

        <div className="metrics">
          <h4>詳細メトリクス</h4>
          <div className="metrics-grid">
            {/* 横ばい期間 (全アルゴリズム共通) */}
            {(detectionResult.metrics.sideways_period_days !== null && detectionResult.metrics.sideways_period_days !== undefined) && (
              <div className="metric-item">
                <span className="metric-label">安定期間:</span>
                <span className="metric-value">{detectionResult.metrics.sideways_period_days}日</span>
              </div>
            )}

            {/* 上昇継続日数 (線形回帰トレンド検出) */}
            {(detectionResult.metrics.trend_consecutive_days !== null && detectionResult.metrics.trend_consecutive_days !== undefined) && (
              <div className="metric-item">
                <span className="metric-label">連続上昇日数:</span>
                <span className="metric-value">{detectionResult.metrics.trend_consecutive_days}日</span>
              </div>
            )}

            {/* 価格上昇率 (全アルゴリズム統一) */}
            {(detectionResult.metrics.price_gain_percent !== null && detectionResult.metrics.price_gain_percent !== undefined) && (
              <div className="metric-item">
                <span className="metric-label">株価上昇率:</span>
                <span className="metric-value">
                  {(() => {
                    const value = formatNumber(detectionResult.metrics.price_gain_percent);
                    if (value === "N/A") return "N/A";
                    const numValue = parseFloat(value);
                    return `${numValue >= 0 ? '+' : ''}${value}%`;
                  })()}
                </span>
              </div>
            )}

            {/* 出来高増加率 (ボリューム考慮型のみ) */}
            {(detectionResult.metrics.volume_increase_percent !== null && detectionResult.metrics.volume_increase_percent !== undefined) && (
              <div className="metric-item">
                <span className="metric-label">出来高増加率:</span>
                <span className="metric-value">
                  {(() => {
                    const value = formatNumber(detectionResult.metrics.volume_increase_percent);
                    if (value === "N/A") return "N/A";
                    const numValue = parseFloat(value);
                    return `${numValue >= 0 ? '+' : ''}${value}%`;
                  })()}
                </span>
              </div>
            )}

            {/* 最大ドローダウン (線形回帰トレンド検出) */}
            {(detectionResult.metrics.trend_max_drawdown_percent !== null && detectionResult.metrics.trend_max_drawdown_percent !== undefined) && (
              <div className="metric-item">
                <span className="metric-label">最大ドローダウン:</span>
                <span className="metric-value">
                  {formatNumber(detectionResult.metrics.trend_max_drawdown_percent) === "N/A" 
                    ? "N/A" 
                    : `${formatNumber(detectionResult.metrics.trend_max_drawdown_percent)}%`
                  }
                </span>
              </div>
            )}

            {/* 出来高情報 (ボリューム考慮型) */}
            {(detectionResult.metrics.volume_baseline_ma !== null && detectionResult.metrics.volume_baseline_ma !== undefined) && (detectionResult.metrics.volume_surge_amount !== null && detectionResult.metrics.volume_surge_amount !== undefined) && (
              <>
                <div className="metric-item">
                  <span className="metric-label">平均出来高:</span>
                  <span className="metric-value">{detectionResult.metrics.volume_baseline_ma.toLocaleString()}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">検出日出来高:</span>
                  <span className="metric-value">{detectionResult.metrics.volume_surge_amount.toLocaleString()}</span>
                </div>
              </>
            )}

            {/* ブレイクアウト情報 */}
            {(detectionResult.metrics.breakout_resistance_level !== null && detectionResult.metrics.breakout_resistance_level !== undefined) && (detectionResult.metrics.breakout_support_level !== null && detectionResult.metrics.breakout_support_level !== undefined) && (
              <>
                <div className="metric-item">
                  <span className="metric-label">上値抵抗線:</span>
                  <span className="metric-value">${detectionResult.metrics.breakout_resistance_level.toFixed(2)}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">下値支持線:</span>
                  <span className="metric-value">${detectionResult.metrics.breakout_support_level.toFixed(2)}</span>
                </div>
              </>
            )}

            {/* ブレイクアウト突破幅 */}
            {(detectionResult.metrics.breakout_penetration_percent !== null && detectionResult.metrics.breakout_penetration_percent !== undefined) && (
              <div className="metric-item">
                <span className="metric-label">抵抗線突破幅:</span>
                <span className="metric-value">
                  {formatNumber(detectionResult.metrics.breakout_penetration_percent)}%
                </span>
              </div>
            )}

            {/* モメンタム情報 */}
            {(detectionResult.metrics.momentum_peak_value !== null && detectionResult.metrics.momentum_peak_value !== undefined) && (
              <>
                <div className="metric-item">
                  <span className="metric-label">最大勢い:</span>
                  <span className="metric-value">
                    {formatNumber(detectionResult.metrics.momentum_peak_value)}
                  </span>
                </div>
                {(detectionResult.metrics.momentum_increase_percent !== null && detectionResult.metrics.momentum_increase_percent !== undefined) && (
                  <div className="metric-item">
                    <span className="metric-label">勢い増加率:</span>
                    <span className="metric-value">
                      {(() => {
                        const value = formatNumber(detectionResult.metrics.momentum_increase_percent);
                        const numValue = parseFloat(value);
                        return `${numValue >= 0 ? '+' : ''}${value}%`;
                      })()}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* 企業情報セクション */}
      <div className="news-section">
        <h3>企業情報・最新トピックス</h3>

        {/* 企業情報取得ボタン */}
        <div className="company-info-controls">
          <button
            onClick={fetchCompanyInfo}
            disabled={companyInfoLoading || !detectionResult}
            className="fetch-company-info-button"
          >
            {companyInfoLoading ? '取得中...' : '銘柄詳細を取得'}
          </button>
          {companyInfo && (
            <span className="company-info-last-updated">
              最終取得: {new Date(companyInfo.last_updated).toLocaleString('ja-JP', {
                timeZone: 'Asia/Tokyo',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </span>
          )}
        </div>

        {/* エラー表示 */}
        {companyInfoError && (
          <div className="company-info-error">
            <p>❌ {companyInfoError}</p>
          </div>
        )}

        {/* 企業情報表示 */}
        {companyInfo && !companyInfoLoading && (
          <div className="company-info-content">
            {/* ビジネス領域・特徴 */}
            <div className="company-section">
              <h4>🏢 ビジネス領域・特徴</h4>
              <p dangerouslySetInnerHTML={{ __html: companyInfo.business_overview.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
            </div>

            {/* 強み */}
            {companyInfo.strengths.length > 0 && (
              <div className="company-section">
                <h4>💪 強み</h4>
                <ul>
                  {companyInfo.strengths.map((strength, index) => (
                    <li key={index} dangerouslySetInnerHTML={{ __html: strength.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                  ))}
                </ul>
              </div>
            )}

            {/* 弱み */}
            {companyInfo.weaknesses.length > 0 && (
              <div className="company-section">
                <h4>⚠️ 弱み</h4>
                <ul>
                  {companyInfo.weaknesses.map((weakness, index) => (
                    <li key={index} dangerouslySetInnerHTML={{ __html: weakness.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                  ))}
                </ul>
              </div>
            )}

            {/* 成長見込み */}
            <div className="company-section">
              <h4>📈 成長見込み</h4>
              <p dangerouslySetInnerHTML={{ __html: companyInfo.growth_outlook.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
            </div>

            {/* 主要リスク */}
            {companyInfo.risks.length > 0 && (
              <div className="company-section">
                <h4>⚡ 主要リスク</h4>
                <ul>
                  {companyInfo.risks.map((risk, index) => (
                    <li key={index} dangerouslySetInnerHTML={{ __html: risk.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                  ))}
                </ul>
              </div>
            )}

            {/* 最近のトピックス */}
            {companyInfo.recent_topics.length > 0 && (
              <div className="company-section">
                <h4>📰 最近のトピックス</h4>
                <div className="recent-topics">
                  {companyInfo.recent_topics.map((topic, index) => (
                    <div key={index} className="topic-item">
                      <h5 dangerouslySetInnerHTML={{ __html: topic.title.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                      {topic.pub_date && (
                        <div className="topic-date">
                          {new Date(topic.pub_date).toLocaleDateString('ja-JP', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      )}
                      <p dangerouslySetInnerHTML={{ __html: topic.summary.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                      {topic.source_url && (
                        <a href={topic.source_url} target="_blank" rel="noopener noreferrer" className="topic-source">
                          ソース: {topic.source_url}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 未取得時のプレースホルダー */}
        {!companyInfo && !companyInfoLoading && !companyInfoError && (
          <div className="news-placeholder">
            <p>📰 上のボタンをクリックして企業情報を取得してください</p>
            <p>OpenAI APIを使用して企業の詳細情報を表示します</p>
          </div>
        )}
      </div>

      {/* アルゴリズム解説セクション（詳細情報として最後に配置） */}
      <div className="algorithm-explanation-section">
        <h3>検出アルゴリズムの詳細</h3>
        
        <div className="algorithm-info">
          <div className="algorithm-header">
            <h4>{algorithmInfo.title}</h4>
            <div className="algorithm-type">
              {getAlgorithmDisplayName(currentAlgorithm)}
            </div>
          </div>
          
          <div className="algorithm-description">
            <h5>検出方法</h5>
            <p>{algorithmInfo.description}</p>
          </div>
          
          <div className="algorithm-characteristics">
            <h5>アルゴリズムの特徴</h5>
            <ul>
              {algorithmInfo.characteristics.map((characteristic: string, index: number) => (
                <li key={index}>{characteristic}</li>
              ))}
            </ul>
          </div>
          
          <div className="algorithm-parameters">
            <h5>主要パラメータの意味</h5>
            <div className="parameter-explanations">
              {(() => {
                const algorithmData = AlgorithmProvider.getAlgorithmByKey(currentAlgorithm);
                if (!algorithmData) return <p>パラメータ情報が取得できません。</p>;

                return algorithmData.parameters_metadata.slice(0, 6).map((param) => (
                  <div key={param.key} className="param-explanation">
                    <span className="param-name">{param.display_name_ja}:</span>
                    <span className="param-description">{param.description || 'このパラメータは' + param.display_name_ja + 'を制御します。'}</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockDetail;